// @file Surface post group by store
// This is new grouping logic that support group by section, color, author, and single-select.
// Only use this store if 'groupPostsV2' flip is enabled, else use surface_sections.ts directly.

import device from '@@/bits/device'
import { postColorText } from '@@/bits/post_color'
import { WallWishGroupByValue } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { PostColorId, WallCustomPostProperty, WallGroupPost } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfacePostGroupByStore = defineStore('surfacePostGroupByStore', () => {
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfaceStore = useSurfaceStore()
  const surfaceMapStore = useSurfaceMapStore()
  const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
  const surfaceOnboardingDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()

  // State
  const groupUnderActionId = ref<WallGroupPost['id'] | null>(null)
  const groupUnderActionRightClickCoordinates = ref<{ x: number; y: number } | null>(null)
  const singleSelectPropertyOptionBeingRenamedId = ref<WallCustomPostProperty['id'] | null>(null)
  const singleSelectPropertyOptionRecentlyCreatedId = ref<WallCustomPostProperty['id'] | null>(null)

  // Getters
  const groupUnderAction = computed(() => getGroupById(groupUnderActionId.value))
  const groupUnderActionTitle = computed(() => {
    if (groupUnderAction.value == null) return null

    if (surfaceWishArrangementStore.isGroupByColor) {
      return postColorText({
        postColor: groupUnderAction.value.id as PostColorId,
        isLightColorScheme: surfaceStore.colorScheme === 'light',
      })
    }

    if ('title' in groupUnderAction.value && groupUnderAction.value.title != null) return groupUnderAction.value.title
    return groupUnderAction.value.id
  })
  const isGroupUnderActionDefault = computed(() => {
    if (surfaceWishArrangementStore.isGroupBySection) {
      return groupUnderActionId.value === surfaceSectionsStore.defaultSectionId
    }

    if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      const singleSelectPropertyOptions = currentSingleSelectProperty.value?.selection_options
      if (singleSelectPropertyOptions == null) return false
      if (isGroupUnderActionIsNoValueGroup.value) {
        const hasNoDefaultOptions = singleSelectPropertyOptions.every((option) => !option.default)
        return hasNoDefaultOptions
      }
      return singleSelectPropertyOptions.find((option) => option.id === groupUnderActionId.value)?.default ?? false
    }

    return false
  })
  const isGroupUnderActionFirstGroup = computed(() => {
    return groupUnderActionId.value === surfaceWishArrangementStore.sortedGroups[0]?.id
  })
  const isGroupUnderActionLastGroup = computed(() => {
    // This only happens when group by single select and post does not have a single select option value
    const currentSortedGroupHasNoValueGroup =
      surfaceWishArrangementStore.sortedGroups[surfaceWishArrangementStore.sortedGroups.length - 1]?.id ===
      WallWishGroupByValue.NoValue

    const lastIndex = currentSortedGroupHasNoValueGroup
      ? surfaceWishArrangementStore.sortedGroups.length - 2
      : surfaceWishArrangementStore.sortedGroups.length - 1

    return groupUnderActionId.value === surfaceWishArrangementStore.sortedGroups[lastIndex]?.id
  })
  const isGroupUnderActionIsNoValueGroup = computed(() => {
    if (!surfaceWishArrangementStore.isGroupBySingleSelect) return false
    return groupUnderActionId.value === WallWishGroupByValue.NoValue
  })
  const currentSingleSelectProperty = computed(() => {
    const customPropId = surfaceWishArrangementStore.customPropIdFromWishGroupBy
    if (customPropId == null) return null
    return surfacePostPropertiesStore.wallSingleSelectPropsByCustomPropertyId[customPropId]
  })
  const groupBeingRenamedId = computed<WallGroupPost['id'] | null>(() => {
    if (surfaceWishArrangementStore.isGroupBySection) {
      return surfaceSectionsStore.sectionBeingRenamedId
    }

    if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      return singleSelectPropertyOptionBeingRenamedId.value ?? null
    }

    return null
  })
  const xGroupSpecificButtons = computed<boolean>(() => {
    if (surfaceStore.isShelf) return false
    if (surfaceStore.isMap) return surfaceMapStore.postListPosition === 'side'
    if (device.touchable) return !surfaceContainerSizeStore.isContainerSmallerThanDesktop
    return !surfaceContainerSizeStore.isContainerSmallerThanTabletPortrait
  })

  const allowOpenGroupActionMenu = computed<boolean>(() => {
    // grid has no group specific buttons so handle accordlingly
    if (surfaceStore.isGrid && surfaceContainerSizeStore.isContainerSmallerThanTabletPortrait) return true
    if (surfaceStore.isGrid && !surfaceContainerSizeStore.isContainerSmallerThanTabletPortrait) return false
    return device.touchable || !xGroupSpecificButtons.value
  })

  const canEditGroupNameInline = computed<boolean>(() => {
    return (
      !allowOpenGroupActionMenu.value &&
      !surfaceOnboardingDemoPadletPanelStore.isDemoPadletPanelDesktop &&
      !surfaceContainerSizeStore.isContainerSmallerThanTabletLandscape &&
      !surfaceStore.isSectionBreakout
    )
  })
  const xGroupRenameModal = computed<boolean>(() => groupBeingRenamedId.value != null && !canEditGroupNameInline.value)
  const isGroupBeingEdited = computed<boolean>(() => groupBeingRenamedId.value != null)
  const isEditingRecentlyCreatedGroup = computed(() => {
    if (surfaceWishArrangementStore.isGroupBySection) {
      return surfaceSectionsStore.sectionRecentlyCreatedId === groupBeingRenamedId.value
    }

    if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      return singleSelectPropertyOptionRecentlyCreatedId.value === groupBeingRenamedId.value
    }

    return false
  })

  // Actions
  const getGroupById = (groupId: WallGroupPost['id'] | null): WallGroupPost | null => {
    return surfaceWishArrangementStore.sortedGroups.find((g) => g.id === groupId) ?? null
  }

  const hideGroupActionMenu = (): void => {
    groupUnderActionId.value = null
    groupUnderActionRightClickCoordinates.value = null
  }

  const startRenamingGroup = (groupId: WallGroupPost['id']): void => {
    if (surfaceWishArrangementStore.isGroupBySection) {
      surfaceSectionsStore.startRenamingSection(groupId as number)
    }

    if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      singleSelectPropertyOptionBeingRenamedId.value = groupId as WallCustomPostProperty['id']
    }
  }

  const stopRenamingGroup = (): void => {
    if (surfaceWishArrangementStore.isGroupBySection) {
      surfaceSectionsStore.stopRenamingSection()
    }

    if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      singleSelectPropertyOptionBeingRenamedId.value = null
    }
  }

  const clearRecentlyCreatedGroup = (): void => {
    singleSelectPropertyOptionRecentlyCreatedId.value = null
    surfaceSectionsStore.sectionRecentlyCreatedId = null
  }

  const updateGroupTitle = (newTitle: string): void => {
    if (groupBeingRenamedId.value == null) return

    if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      if (currentSingleSelectProperty.value == null) return

      surfacePostPropertiesStore.updateSingleSelectPropertyOptionName({
        propertyId: currentSingleSelectProperty.value.id,
        optionId: groupBeingRenamedId.value as string,
        optionName: newTitle,
      })

      stopRenamingGroup()
    } else {
      const edited = surfaceSectionsStore.editSectionTitle({
        sectionId: groupBeingRenamedId.value as number,
        title: newTitle,
      })
      if (edited) {
        stopRenamingGroup()
      }
    }
  }

  const createGroupBefore = async (groupId: WallGroupPost['id']): Promise<void> => {
    if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      if (currentSingleSelectProperty.value == null) return
      const newOptionId = await surfacePostPropertiesStore.createSingleSelectPropertyOptionBefore({
        propertyId: currentSingleSelectProperty.value.id,
        optionId: groupId as string,
      })
      if (newOptionId == null) return
      singleSelectPropertyOptionRecentlyCreatedId.value = newOptionId
      startRenamingGroup(newOptionId)
    } else {
      surfaceSectionsStore.createSectionBefore(groupId as number)
    }
  }

  const createGroupAfter = async (groupId: WallGroupPost['id']): Promise<void> => {
    if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      if (currentSingleSelectProperty.value == null) return
      const newOptionId = await surfacePostPropertiesStore.createSingleSelectPropertyOptionAfter({
        propertyId: currentSingleSelectProperty.value.id,
        optionId: groupId as string,
      })
      if (newOptionId == null) return
      singleSelectPropertyOptionRecentlyCreatedId.value = newOptionId
      startRenamingGroup(newOptionId)
    } else {
      surfaceSectionsStore.createSectionAfter(groupId as number)
    }
  }

  return {
    // State
    groupUnderActionId,
    groupUnderActionRightClickCoordinates,
    singleSelectPropertyOptionRecentlyCreatedId,

    // Getters
    groupUnderAction,
    groupUnderActionTitle,
    isGroupUnderActionDefault,
    isGroupUnderActionFirstGroup,
    isGroupUnderActionLastGroup,
    isGroupUnderActionIsNoValueGroup,
    currentSingleSelectProperty,
    groupBeingRenamedId,
    xGroupSpecificButtons,
    canEditGroupNameInline,
    xGroupRenameModal,
    isGroupBeingEdited,
    isEditingRecentlyCreatedGroup,

    // Actions
    getGroupById,
    hideGroupActionMenu,
    startRenamingGroup,
    stopRenamingGroup,
    clearRecentlyCreatedGroup,
    updateGroupTitle,
    createGroupBefore,
    createGroupAfter,
  }
})
